import { useState } from 'react';
import { Geolocation } from '@capacitor/geolocation';

interface GeolocationState {
  isDetecting: boolean;
  error: string | null;
}

interface LocationResult {
  location: string;
  latitude: number;
  longitude: number;
}

export const useGeolocation = () => {
  const [state, setState] = useState<GeolocationState>({
    isDetecting: false,
    error: null,
  });

  const detectLocation = async (): Promise<LocationResult | null> => {
    setState({ isDetecting: true, error: null });

    try {
      // Request permission and get coordinates
      const coordinates = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
      });

      const lat = coordinates.coords.latitude;
      const lon = coordinates.coords.longitude;

      // Convert coordinates to address using Nominatim (OpenStreetMap)
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&addressdetails=1`
      );
      
      if (!response.ok) {
        throw new Error('Error al obtener la dirección');
      }

      const data = await response.json();

      if (data && data.display_name) {
        // Extract more readable location (city, country)
        const address = data.address;
        let location = '';
        
        if (address) {
          const city = address.city || address.town || address.village || address.municipality;
          const state = address.state || address.province;
          const country = address.country;
          
          if (city && country) {
            location = state ? `${city}, ${state}, ${country}` : `${city}, ${country}`;
          } else {
            location = data.display_name;
          }
        } else {
          location = data.display_name;
        }

        setState({ isDetecting: false, error: null });
        
        return {
          location,
          latitude: lat,
          longitude: lon
        };
      } else {
        throw new Error('No se pudo encontrar la dirección');
      }
    } catch (error: any) {
      let errorMessage = 'GPS detection failed';
      
      if (error.message?.includes('permission') || error.message?.includes('denied')) {
        errorMessage = 'Location permission denied';
      } else if (error.message?.includes('timeout')) {
        errorMessage = 'GPS timeout - Make sure GPS is enabled';
      } else if (error.message?.includes('network')) {
        errorMessage = 'Network error - Check your internet';
      } else if (error.message?.includes('unavailable')) {
        errorMessage = 'GPS unavailable on this device';
      }

      setState({ isDetecting: false, error: errorMessage });
      return null;
    }
  };

  return {
    detectLocation,
    isDetecting: state.isDetecting,
    error: state.error,
  };
};